import type { BlockAnimation } from '@shared/protocol';

/** Animation kinds that can be frozen independently at runtime. */
export const ANIMATION_KINDS = ['sequence', 'parametric', 'oscillator'] as const;
export type AnimationKind = (typeof ANIMATION_KINDS)[number];

export type AnimationFlags = Record<AnimationKind, boolean>;

export const DEFAULT_ANIMATION_FLAGS: AnimationFlags = {
  sequence: true,
  parametric: true,
  oscillator: true,
};

export function animationsEqual(a: AnimationFlags, b: AnimationFlags): boolean {
  return ANIMATION_KINDS.every((kind) => a[kind] === b[kind]);
}

/**
 * Per-kind freeze gate: disabled kinds keep using the tick captured at disable
 * time so sequence / parametric / oscillator can be toggled independently.
 */
export class AnimationTickGate {
  private enabled: AnimationFlags = { ...DEFAULT_ANIMATION_FLAGS };
  private readonly frozen: Partial<Record<AnimationKind, number>> = {};

  get flags(): Readonly<AnimationFlags> {
    return this.enabled;
  }

  /** Returns true when placements using affected kinds should rebuild. */
  setFlags(flags: AnimationFlags, worldTick: number): boolean {
    let dirty = false;
    for (const kind of ANIMATION_KINDS) {
      if (flags[kind]) {
        if (this.frozen[kind] !== undefined) {
          delete this.frozen[kind];
          dirty = true;
        }
      } else if (this.frozen[kind] === undefined) {
        this.frozen[kind] = worldTick;
        dirty = true;
      }
    }
    this.enabled = { ...flags };
    return dirty;
  }

  isKindEnabled(kind: AnimationKind): boolean {
    return this.enabled[kind];
  }

  isAnimationEnabled(animation: BlockAnimation | null | undefined): boolean {
    if (!animation) return true;
    return this.enabled[animation.type as AnimationKind];
  }

  tickFor(animation: BlockAnimation | null | undefined, worldTick: number): number {
    if (!animation) return worldTick;
    const frozen = this.frozen[animation.type as AnimationKind];
    return frozen !== undefined ? frozen : worldTick;
  }
}
